import React, { useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { INDIAN_STATES, getStateByCode } from '../../lib/states';
import { validateGSTIN } from '../../lib/gstin';
import { Check, X, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { local } from '../../lib/storage';
import { validatePhone } from '../../lib/validate';
import { useLanguage } from '../../contexts/LanguageContext';

const defaultCustomer = { name: '', gstin: '', address: '', state: '', stateName: '', phone: '', email: '' };

export default function CustomerSection({ customer, setCustomer, savedCustomers = [], onSaveCustomer }) {
  const { t } = useLanguage();
  const gstinCheck = useMemo(() => customer.gstin ? validateGSTIN(customer.gstin) : null, [customer.gstin]);
  const requiredClass = (value) => String(value || '').trim()
    ? ''
    : 'border-red-200 bg-red-50/30 focus-visible:ring-red-200';
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');

  React.useEffect(() => {
    if (!gstinCheck?.valid) return;
    const st = getStateByCode(gstinCheck.stateCode);
    if (st && st.short !== customer.state) {
      setCustomer((c) => ({ ...c, state: st.short, stateName: st.name }));
    }
  }, [gstinCheck?.valid, gstinCheck?.stateCode]);

  const update = (patch) => setCustomer((c) => ({ ...c, ...patch }));

  const filtered = savedCustomers.filter((c) => {
    const q = search.toLowerCase();
    return [c.name, c.gstin, c.phone].some((v) => String(v || '').toLowerCase().includes(q));
  });

  const pick = (c) => {
    setCustomer({ ...c });
    setPickerOpen(false);
    setSearch('');
  };

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 shadow-sm" data-testid="customer-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-slate-900">{t('billTo')}</h3>
        <div className="flex items-center gap-2">
          {savedCustomers.length > 0 && (

            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs" data-testid="customer-picker-btn">
                  {t('pickSaved')} <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="end">
                <div className="p-2 border-b">
                  <Input placeholder={t('search')} value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 text-sm" data-testid="customer-picker-search" />
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {filtered.length === 0 && <div className="p-3 text-xs text-slate-500">{t('noCustomersSaved')}</div>}
                  {filtered.map((c) => (
                    <button key={c.id || c.name} onClick={() => pick(c)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100">
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {[c.stateName || c.state, c.gstin, c.phone].filter(Boolean).join(' · ')}
                      </div>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
          <Button variant="ghost" size="sm" className="h-6 text-xs text-slate-400 hover:text-slate-600 px-2" onClick={() => setCustomer(defaultCustomer)} data-testid="clear-customer-btn">
            {t('clearCustomer')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t('customerName')}</Label>
          <Input data-testid="customer-name-input" value={customer.name} onChange={(e) => update({ name: e.target.value })} placeholder={t('clientPlaceholder')} className={requiredClass(customer.name)} />
        </div>
        <div>
          <Label>{t('gstinOptional')}</Label>
          <div className="relative">
            <Input data-testid="customer-gstin-input" value={customer.gstin || ''} onChange={(e) => update({ gstin: e.target.value.toUpperCase() })} placeholder="29AAAAA0000A1Z5" maxLength={15} className="font-mono uppercase pr-8" />
            {customer.gstin?.length === 15 && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                {gstinCheck?.valid
                  ? <Check className="w-4 h-4 text-emerald-600" />
                  : <X className="w-4 h-4 text-red-500" />}
              </div>
            )}
          </div>
          {customer.gstin?.length === 15 && !gstinCheck?.valid && (
            <p className="text-xs text-red-500 mt-1">{gstinCheck?.reason}</p>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t('placeOfSupply')}</Label>
          <Select value={customer.state} onValueChange={(v) => {
            const st = INDIAN_STATES.find((s) => s.short === v);
            update({ state: v, stateName: st?.name || '' });
          }}>
            <SelectTrigger data-testid="customer-state-select">
              <SelectValue placeholder={t('selectState')} />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {INDIAN_STATES.map((s) => (
                <SelectItem key={s.short} value={s.short}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t('emailOptional')}</Label>
          <Input type="email" data-testid="customer-email-input" value={customer.email || ''} onChange={(e) => update({ email: e.target.value })} placeholder="client@company.com" />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t('phoneWhatsapp')}</Label>
          <Input
            data-testid="customer-phone-input"
            value={customer.phone || ''}
            onChange={(e) => update({ phone: e.target.value })}
            placeholder="9876543210"
            className={validatePhone(customer.phone) ? 'border-red-400 focus-visible:ring-red-300' : requiredClass(customer.phone)}
          />
          {validatePhone(customer.phone) && <p className="text-xs text-red-500 mt-1">{validatePhone(customer.phone)}</p>}
        </div>
      </div>

      <div className="mt-4">
        <Label>{t('address')}</Label>
        <Textarea data-testid="customer-address-input" rows={2} value={customer.address || ''} onChange={(e) => update({ address: e.target.value })} placeholder={t('streetPlaceholder')} />
      </div>

      <div className="mt-3 flex justify-end">
        <Button variant="ghost" size="sm" className="text-xs h-8" onClick={() => customer.name && onSaveCustomer && onSaveCustomer(customer)} data-testid="save-customer-btn">
          {t('saveCustomerLater')}
        </Button>
      </div>
    </section>
  );
}
